import {
  EventInterceptionPlugin,
  FlagOverridePlugin,
} from "@launchdarkly/toolbar/plugins";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { type ComponentType, type ReactNode, Suspense, lazy } from "react";

function readClientSideId(): string {
  const viteEnv = import.meta.env as Record<string, string | undefined>;
  return (viteEnv.VITE_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID ?? "").trim();
}

/** When manifests or `.env` set `VITE_PUBLIC_LAUNCHDARKLY_TOOLBAR=true` (and client ID is set). */
function shouldEnableLaunchDarklyToolbar(clientSideId: string): boolean {
  if (!clientSideId) return false;
  const viteEnv = import.meta.env as Record<string, string | undefined>;
  const toolbar = (viteEnv.VITE_PUBLIC_LAUNCHDARKLY_TOOLBAR ?? "").trim();
  return toolbar === "true";
}

// Anonymous bootstrap context; Auth0-based identification can be wired later
// via ldClient.identify() once the user profile is loaded.
const initialLDContext = {
  kind: "user" as const,
  key: "anonymous",
  anonymous: true,
};

function PassThrough({ children }: { children: ReactNode }) {
  return children;
}

export type LaunchDarklyBootstrap = {
  LaunchDarklyProvider: ComponentType<{ children: ReactNode }>;
  /** LD override toolbar slot; null without client ID or when `VITE_PUBLIC_LAUNCHDARKLY_TOOLBAR` is not `true`. */
  launchDarklyDevToolbarSlot: ReactNode;
};

/**
 * Initializes LaunchDarkly React provider (and dev toolbar when applicable).
 * Call once from the app entry before `createRoot().render(...)`.
 */
export async function bootstrapLaunchDarkly(): Promise<LaunchDarklyBootstrap> {
  const clientSideId = readClientSideId();

  const ldDevToolbarPlugins = shouldEnableLaunchDarklyToolbar(clientSideId)
    ? {
        flagOverridePlugin: new FlagOverridePlugin({
          storageNamespace: "core-tasks-web-ld-overrides",
        }),
        eventInterceptionPlugin: new EventInterceptionPlugin(),
      }
    : null;

  const LazyLaunchDarklyDevToolbar = ldDevToolbarPlugins
    ? lazy(() => import("@components/dev/LaunchDarklyDevToolbar"))
    : null;

  const LaunchDarklyProvider = clientSideId
    ? await asyncWithLDProvider({
        clientSideID: clientSideId,
        context: initialLDContext,
        timeout: 5,
        reactOptions: {
          useCamelCaseFlagKeys: false,
        },
        options: ldDevToolbarPlugins
          ? {
              plugins: [
                ldDevToolbarPlugins.flagOverridePlugin,
                ldDevToolbarPlugins.eventInterceptionPlugin,
              ],
            }
          : undefined,
      })
    : PassThrough;

  if (!clientSideId) {
    console.warn(
      "LaunchDarkly: VITE_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID is not set; " +
        "flags will use their fallback values.",
    );
  }

  const launchDarklyDevToolbarSlot =
    LazyLaunchDarklyDevToolbar && ldDevToolbarPlugins ? (
      <Suspense fallback={null}>
        <LazyLaunchDarklyDevToolbar
          flagOverridePlugin={ldDevToolbarPlugins.flagOverridePlugin}
          eventInterceptionPlugin={ldDevToolbarPlugins.eventInterceptionPlugin}
        />
      </Suspense>
    ) : null;

  return { LaunchDarklyProvider, launchDarklyDevToolbarSlot };
}
